import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { checkSubscriptionStatus } from '../utils/subscriptionCheck';

const DISMISS_KEY = 'trialBannerDismissedAt';
const STATUS_TTL_MS = 5 * 60 * 1000;

// checkSubscriptionStatus() only falls back to its localStorage cache when the
// request FAILS - the success path always hits /api/team/me and
// /api/subscriptions/trial-status. Since this banner mounts on every
// authenticated route, calling it per navigation would add two API round-trips
// to every page change for every user. Memoise the in-flight promise and its
// result so we hit the network at most once per TTL per tab.
let statusCache = { at: 0, promise: null };

// The memo lives at module scope, so it outlives a logout (which is client-side
// with no reload). Without this, signing in as a different user inside the TTL
// showed them the previous user's trial state.
if (typeof window !== 'undefined') {
  const invalidate = () => {
    statusCache = { at: 0, promise: null };
  };
  window.addEventListener('auth:cleared', invalidate);
  window.addEventListener('accountChanged', invalidate);
}

function getSubscriptionStatusCached() {
  const now = Date.now();
  if (statusCache.promise && now - statusCache.at < STATUS_TTL_MS) {
    return statusCache.promise;
  }
  const promise = checkSubscriptionStatus().catch((error) => {
    // Don't poison the cache with a rejection.
    statusCache = { at: 0, promise: null };
    throw error;
  });
  statusCache = { at: now, promise };
  return promise;
}

/**
 * Persistent trial / post-trial upgrade surface.
 *
 * Before this existed, the ONLY place a trial countdown appeared anywhere in
 * the app was Settings > Billing — two clicks deep, owner-only. Trials expired
 * without the user ever seeing a price or a clock. This mounts in the
 * authenticated layout so every page shows the remaining time and a way to pay.
 *
 * Dismissal is soft: it lasts for the session, and is ignored once the trial is
 * nearly over or has already lapsed.
 */
export default function TrialStatusBanner() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(
    () => !!sessionStorage.getItem(DISMISS_KEY),
  );

  useEffect(() => {
    let cancelled = false;
    getSubscriptionStatusCached()
      .then((result) => {
        if (!cancelled) setStatus(result);
      })
      .catch(() => {
        // Never let a banner break the page it sits on.
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  if (!status) return null;
  if (status.isTeamMember) return null;
  if (status.hasActiveSubscription) return null;

  const isOnTrial = status.isOnTrial;
  const isLapsed = status.isFreeTier && !isOnTrial;
  if (!isOnTrial && !isLapsed) return null;

  const daysRemaining = Number(status.daysRemaining ?? 0);
  // Only the closing days of a live trial are undismissible - that window ends
  // on its own. `isLapsed` was included here originally, which made the banner
  // permanent and un-hideable on every page for every post-trial free account,
  // since that state never expires.
  const urgent = isOnTrial && daysRemaining <= 3;
  if (dismissed && !urgent) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  const tone = urgent
    ? 'bg-amber-50 border-amber-300 text-amber-900'
    : 'bg-indigo-50 border-indigo-200 text-indigo-900';

  const headline = isLapsed
    ? 'Your full access has ended'
    : daysRemaining <= 1
      ? 'Your full access ends today'
      : `${daysRemaining} days left of full access`;

  const detail = isLapsed
    ? 'Your dashboard and uploads stay free. Vehicle management, co-host tools and the finance hub need a paid plan.'
    : 'Vehicle management, co-host tools and the finance hub need a paid plan when your trial ends.';

  return (
    <div
      className={`mb-4 rounded-lg border px-4 py-3 flex flex-wrap items-center justify-between gap-3 ${tone}`}
      role="status"
    >
      <div className="min-w-0">
        <p className="font-semibold text-sm">{headline}</p>
        <p className="text-xs opacity-90">{detail}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {!urgent && (
          <button
            type="button"
            onClick={handleDismiss}
            className="text-xs font-medium px-3 py-2 rounded-md hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-indigo-500"
          >
            Not now
          </button>
        )}
        <button
          type="button"
          onClick={() => navigate('/settings/billing')}
          className="text-xs font-semibold px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-indigo-500"
        >
          {isLapsed ? 'Restore full access' : 'Choose a plan'}
        </button>
      </div>
    </div>
  );
}
